import React, { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { IVisionProfilePageProps } from './vision-profile-page.models';
import { useGoBack } from '../../../../../hooks/use-go-back';
import { Box, ResponsiveMargins } from '../../../../common/box';
import { Preloader } from '../../../../common/preloader';
import { PersonalInfo } from './personal-info';
import { Activities } from './activities/activities';
import { useStyles } from './vision-profile-page.styles';
import { VisionProfileIncomplete } from './vision-profile-incomplete/vision-profile-incomplete';
import { useVisionProfileData, VPStatus } from '../../../../../hooks/use-vision-profile-data';
import { ActivitiesChangeButton } from './activities-change-button';
import { Page } from '../../page/page';

export const VisionProfilePage = ({
  visionProfileData, isError, isLoading,
}: IVisionProfilePageProps): JSX.Element => {
  const { status } = useVisionProfileData(visionProfileData);
  const { goBack } = useGoBack();
  const classes = useStyles();
  const intl = useIntl();

  const handleBack = () => goBack();

  const renderVisionProfilePage = () => {
    if (isLoading) {
      return <Preloader />;
    }

    if (isError) {
      handleBack();
    }

    return (
      <Page
        title={intl.formatMessage({ id: 'vpPage.title' })}
        description={intl.formatMessage({ id: 'vpPage.description' })}
      >
        {status === VPStatus.INCOMPLETE && <VisionProfileIncomplete />}
        {status === VPStatus.COMPLETE && (
          <>
            <Box
              responsiveMargins={ResponsiveMargins.sm}
              title={(
                <span data-testid="vision-profile-title" className={classes.title}>
                  {intl.formatMessage(
                    { id: 'vp.page.title' },
                    { sup: (chunks: ReactNode) => <sup>{chunks}</sup> },
                  )}
                </span>
              )}
              isBackButton
            >
              <h2 className={classes.subtitle} data-testid="subtitle-personal-info">
                <FormattedMessage id="vp.page.subtitle.personalInfo" />
              </h2>
              <PersonalInfo visionProfileData={visionProfileData!.visionProfile!} />
              <div className={classes.subtitleBar} data-testid="subtitle-bar">
                <h2 className={classes.subtitle} data-testid="subtitle-activities">
                  <FormattedMessage id="vp.page.subtitle.activities" />
                </h2>
                <ActivitiesChangeButton
                  whatDoYouLikeCustom={visionProfileData!.visionProfile!.whatDoYouLikeCustom}
                  whatDoYouLikePredefined={visionProfileData!.visionProfile!.whatDoYouLikePredefined}
                />
              </div>
              <Activities visionProfileData={visionProfileData!.visionProfile!} />
            </Box>
          </>
        )}
      </Page>
    );
  };

  return renderVisionProfilePage();
};
